import { ProjectRoleId } from "@clovis/server/src/app/domain/project/role";
import * as React from "react";
import type { Options } from "react-select/dist/declarations/src/types";
import type { RefCallBack } from "~/config/react-hook-form";
import type {
  BasicMultiSelectFieldInputOption,
  BasicMultiSelectFieldInputOptionType,
  BasicMultiSelectFieldInputProps,
} from "~/screens/App/components/BasicMultiSelectFieldInput/BasicMultiSelectFieldInput";
import { BasicMultiSelectFieldInput } from "~/screens/App/components/BasicMultiSelectFieldInput/BasicMultiSelectFieldInput";
import { createComponentHook } from "~/types";
import type { UserFieldsFragment } from "./AssigneesMultiSelectFieldInput.graphql";
import {
  useGetTeamsQuery,
  useGetUsersQuery,
} from "./AssigneesMultiSelectFieldInput.graphql";

type AssignationOptionType = {
  id: string;
  type: "orgs" | "teams" | "users";
};

type UserSubscribersType = {
  avatar: string | null | undefined;
  label: string;
  color: string;
  type: BasicMultiSelectFieldInputOptionType;
  value: AssignationOptionType;
};

const getOrgsOptions = (users: UserFieldsFragment[]) => {
  const membersOrgsOptions = users
    .filter((user) => user.active_org)
    .map((user) => ({
      avatar: user.active_org?.org?.org_avatar?.public_url,
      color: "",
      label: user.active_org ? user.active_org.org.name : "",
      type: "org" as BasicMultiSelectFieldInputOptionType,
      value: {
        id: user.active_org ? user.active_org.org.id : "",
        type: "orgs" as AssignationOptionType["type"],
      },
    }));

  const uniqueOrgs = membersOrgsOptions?.filter(
    (option, index, array) =>
      index === array.findIndex((value) => option.value.id === value.value.id)
  );

  return uniqueOrgs ?? [];
};

const useUserMultiSelectFieldInput = createComponentHook(
  (props: AssigneesMultiSelectFieldInputProps) => {
    const [disabledProjectUsers, setDisabledProjectUsers] = React.useState<
      UserFieldsFragment[]
    >([]);
    const [activeProjectUsers, setActiveProjectUsers] = React.useState<
      UserFieldsFragment[]
    >([]);

    const [userOptions, setUserOptions] = React.useState<UserSubscribersType[]>(
      []
    );

    const [orgsOptions, setOrgsOptions] = React.useState<UserSubscribersType[]>(
      []
    );

    const [disabledOptions, setDisabledOptions] = React.useState<
      UserSubscribersType[]
    >([]);

    // fetch users non disabled from the project
    const [{ data: projectUsers }] = useGetUsersQuery({
      requestPolicy: "cache-only",
      variables: {
        id: props.projectId,
      },
    });

    const [{ data: projectTeams }] = useGetTeamsQuery({
      requestPolicy: "cache-only",
      variables: {
        id: props.projectId,
      },
    });

    React.useEffect(() => {
      setDisabledProjectUsers(
        projectUsers?.project?.members
          ?.filter((member) => {
            return member.role.name === ProjectRoleId.Disabled;
          })
          ?.map((member) => member.user) ?? []
      );

      setActiveProjectUsers(
        projectUsers?.project?.members
          ?.filter((member) => {
            return member.role.name !== ProjectRoleId.Disabled;
          })
          .map((member) => member.user) ?? []
      );
    }, [projectUsers]);

    const teamsOptions =
      projectTeams?.project?.teams?.map((t) => ({
        avatar: undefined,
        color: t.color,
        label: t.name,
        type: "team" as BasicMultiSelectFieldInputOptionType,
        value: { id: t.id, type: "teams" as AssignationOptionType["type"] },
      })) ?? [];

    React.useEffect(() => {
      const assignedUsers = (
        props.control._formValues?.assignations as Record<
          "orgs" | "teams" | "users",
          string[]
        >
      )?.users;

      const disabledUsers = disabledProjectUsers?.filter((disabledUser) => {
        return assignedUsers.includes(disabledUser.id);
      });

      const disabledUserOptions =
        disabledUsers.map((disabledUser) => {
          return {
            avatar: disabledUser?.user_avatar?.public_url,
            color: "",
            label: disabledUser.full_name!,
            type: "user" as BasicMultiSelectFieldInputOptionType,
            value: {
              id: disabledUser.id,
              type: "users" as AssignationOptionType["type"],
            },
          };
        }) ?? [];

      const activeOrgOptions = getOrgsOptions(activeProjectUsers);
      const disabledOrgsOptions = getOrgsOptions(disabledUsers).filter(
        (org) => {
          return !activeOrgOptions.find(
            (activeOrg) => activeOrg.value.id === org.value.id
          );
        }
      ); // this filter guarantees that the orgs from actives users are not disabled

      setUserOptions(
        disabledUserOptions.concat(
          activeProjectUsers.map((u) => ({
            avatar: u?.user_avatar?.public_url,
            color: "",
            label: u.full_name!,
            type: "user" as BasicMultiSelectFieldInputOptionType,
            value: {
              id: u.id,
              type: "users" as AssignationOptionType["type"],
            },
          }))
        )
      );

      setOrgsOptions(disabledOrgsOptions.concat(activeOrgOptions));

      setDisabledOptions(disabledUserOptions.concat(disabledOrgsOptions));
    }, [disabledProjectUsers, activeProjectUsers]);

    return {
      state: {
        disabledOptions,
        orgs: orgsOptions,
        teams: teamsOptions,
        users: userOptions,
      },
    };
  }
);

type AssigneesMultiSelectFieldInputProps = Omit<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  BasicMultiSelectFieldInputProps<any>,
  "options"
> & {
  projectId: string;
  onSelect?: (
    value: Options<BasicMultiSelectFieldInputOption<AssignationOptionType>>
  ) => void;
};

const AssigneesMultiSelectFieldInput = React.forwardRef<
  RefCallBack,
  AssigneesMultiSelectFieldInputProps
>(function AssigneesMultiSelectFieldInput(props, ref) {
  const { state } = useUserMultiSelectFieldInput(props);
  return (
    <BasicMultiSelectFieldInput
      autoFocus={props.autoFocus}
      options={state.orgs.concat(state.teams, state.users)}
      onSelect={props.onSelect}
      disabledOptions={state.disabledOptions}
      compareValue={(
        assignations: { orgs: string[]; teams: string[]; users: string[] },
        value: AssignationOptionType
      ) => {
        return assignations[value.type]?.includes(value.id);
      }}
      {...props}
      ref={ref as React.ForwardedRef<RefCallBack> & RefCallBack}
    />
  );
});

export { AssigneesMultiSelectFieldInput, getOrgsOptions };
export type { AssignationOptionType };
